import axios from 'axios';

const ACCESS_TOKEN_KEY = 'healthera_support_token';
const REFRESH_TOKEN_KEY = 'healthera_support_refresh_token';
const SESSION_KEYS = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, 'user_id', 'orgID', 'userRole', 'roles'];
const EXPIRY_MARGIN_SECONDS = 60;
const REFRESH_LOCK = 'pam-session-refresh';
// The refresh holds the cross-tab lock for its whole duration, so a stalled one must give up
// rather than queue every tab's requests behind it. A timeout is a temporary failure: the
// session survives it.
const REFRESH_TIMEOUT_MS = 15_000;

export const API_VERSION = '3.6.0';

/** Legacy error codes that mean the session is over: the token is invalid, expired or revoked. */
const SESSION_REJECTED_CODES = ['4000', '4001', '4004'];

export const isSessionRejectedCode = (code?: string): boolean => Boolean(code && SESSION_REJECTED_CODES.includes(code));

// The Web Locks API is missing from this TypeScript version's DOM typings.
interface SessionLockManager {
  request<T>(name: string, task: () => Promise<T>): Promise<T>;
}

declare global {
  interface Navigator {
    readonly locks?: SessionLockManager;
  }
}

export const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  responseType: 'json',
  timeout: REFRESH_TIMEOUT_MS,
  headers: { 'client-id': process.env.REACT_APP_CLIENT_ID },
});

let refreshing: Promise<string | null> | null = null;

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

export const hasSession = (): boolean => Boolean(getAccessToken() || getRefreshToken());

export const storeTokens = ({ token, refresh_token }: { token?: string; refresh_token?: string }) => {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
};

/** Forgets everything sign-in stored. Callers decide whether to navigate to the login page. */
export const endSession = () => {
  SESSION_KEYS.forEach(key => localStorage.removeItem(key));
  refreshing = null;
};

const expiryOf = (token: string): number | undefined => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).exp;
  } catch {
    return undefined;
  }
};

const isFresh = (token: string | null): token is string => {
  const expiry = token ? expiryOf(token) : undefined;
  return typeof expiry === 'number' && expiry > Date.now() / 1000 + EXPIRY_MARGIN_SECONDS;
};

const isRefreshRejected = (error: any) =>
  [401, 403].includes(error?.response?.status) || isSessionRejectedCode(error?.response?.data?.error?.code);

const sessionAfterRejection = (usedRefreshToken: string): string | null => {
  // Signed in or out from another tab meanwhile: keep whatever that left behind.
  if (getRefreshToken() !== usedRefreshToken) return getAccessToken();
  endSession();
  return null;
};

// Legacy accepts a reused refresh token for a short grace period but does not persist the pair it
// hands back, so two tabs refreshing with the same token would leave one holding a pair the next
// refresh rejects. The lock serialises refreshes across tabs; the fallback covers browsers
// without Web Locks, where only the in-tab `refreshing` promise applies.
const withRefreshLock = <T>(task: () => Promise<T>): Promise<T> =>
  navigator.locks ? navigator.locks.request(REFRESH_LOCK, task) : task();

/**
 * Swaps the refresh token for a new pair. `staleToken` is the access token the caller found
 * unusable: once the lock is held, a different token in storage means another tab or an earlier
 * request already renewed (or signed out), so that one is returned instead of rotating again.
 */
const refresh = (staleToken: string | null): Promise<string | null> =>
  withRefreshLock(async () => {
    if (getAccessToken() !== staleToken) return getAccessToken();

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      endSession();
      return null;
    }

    let response;
    try {
      response = await authApi.post(
        '/tokens',
        { refresh_token: refreshToken },
        { headers: { 'api-version': API_VERSION } }
      );
    } catch (error) {
      if (isRefreshRejected(error)) return sessionAfterRejection(refreshToken);
      throw error;
    }

    const renewed = response.data?.data?.[0];
    if (!renewed?.token) {
      // Legacy answers most errors with HTTP 200 and an error body; only a session code means
      // the refresh token itself was rejected. Anything else is a temporary failure.
      const error = response.data?.error;
      if (isSessionRejectedCode(error?.code)) return sessionAfterRejection(refreshToken);
      throw new Error(error?.message ?? 'Refresh returned no token');
    }
    // Signed out from another tab while this was in flight: don't bring the session back.
    if (getRefreshToken() !== refreshToken) return getAccessToken();

    storeTokens(renewed);
    return renewed.token;
  });

export const renewAccessToken = (staleToken: string | null): Promise<string | null> => {
  if (!refreshing) {
    refreshing = refresh(staleToken).finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
};

export const freshAccessToken = async (): Promise<string | null> => {
  const accessToken = getAccessToken();
  if (isFresh(accessToken)) return accessToken;
  return renewAccessToken(accessToken);
};
